import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import { NounKeys } from '../../../../constants/nouns';
import {
	type ProductKeys,
	ProductKeys as ProductKeysObject,
} from '../../../../constants/product-keys';
import {
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../../constants/schemas/3p-config';
import { isProductConnected } from '../../../../utils/connectors';
import type { Sections } from './dynamic-sections';
import { getNestedProducts } from './get-nested-products';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { sortByResultCount } from './sort-by-result-count';

// Module-level constant: noun values should never be treated as nested products
const NOUN_KEYS_SET = new Set<string>(Object.values(NounKeys));

// Single-select filter option: ~32px (space.400) + ~8px padding
const DEFAULT_APP_FILTER_ITEM_HEIGHT_PX = 40;
const DEFAULT_APP_FILTER_GAP_PX = 4;
// Reserved for: "All" option, section headers, padding, Show More, description,
// plus "Improve search Give feedback" footer (marginTop + paddingBlock + content ~48px)
const DEFAULT_RESERVED_HEIGHT_PX = 260;
// Accordions and fanout expand 1 option to multiple rows; reduce slots to prevent overflow
const SLOT_SAFETY_FACTOR = 0.75;

export type SectionVisibilityLimits = {
	connectedMax: number;
	unconnectedMax: number;
};

export type GetSectionVisibilityLimitsOptions = {
	connectedCount: number;
	unconnectedCount: number;
	ratio?: [number, number];
	itemHeight?: number;
	gap?: number;
	reservedHeight?: number;
	slotSafetyFactor?: number;
};

/**
 * Calculates how many apps to show in each section (connected vs unconnected) for single-select
 * app filters, maximising visible items while respecting a 2:1 ratio (favouring connected) and
 * the user's viewport/available height.
 *
 * Handles overflow: if one section has fewer apps than its allocation, the remainder
 * goes to the other section.
 */
export const getSectionVisibilityLimits = (
	availableHeight: number,
	options: GetSectionVisibilityLimitsOptions,
): SectionVisibilityLimits => {
	const {
		connectedCount,
		unconnectedCount,
		ratio = [2, 1],
		itemHeight = DEFAULT_APP_FILTER_ITEM_HEIGHT_PX,
		gap = DEFAULT_APP_FILTER_GAP_PX,
		reservedHeight = DEFAULT_RESERVED_HEIGHT_PX,
		slotSafetyFactor = SLOT_SAFETY_FACTOR,
	} = options;

	const [ratioConnected, ratioUnconnected] = ratio;
	const ratioTotal = ratioConnected + ratioUnconnected;

	// Fallback for invalid or very small viewport
	if (availableHeight <= 0 || reservedHeight >= availableHeight) {
		return {
			connectedMax: Math.min(2, connectedCount),
			unconnectedMax: Math.min(1, unconnectedCount),
		};
	}

	const usableHeight = Math.max(0, availableHeight - reservedHeight);
	// Same formula as get-max-results-to-show: slots = (height + gap) / (itemHeight + gap)
	// Apply safety factor: accordions and fanout expand 1 option to multiple rows
	const totalSlots = Math.max(
		1,
		Math.floor(((usableHeight + gap) / (itemHeight + gap)) * slotSafetyFactor),
	);

	if (totalSlots <= 0) {
		return {
			connectedMax: Math.min(1, connectedCount),
			unconnectedMax: Math.min(1, unconnectedCount),
		};
	}

	// Allocate by ratio: connected gets 2/3, unconnected gets 1/3
	let connectedSlots = Math.ceil((totalSlots * ratioConnected) / ratioTotal);
	let unconnectedSlots = totalSlots - connectedSlots;

	// Cap by actual counts and redistribute surplus
	let connectedMax = Math.min(connectedSlots, connectedCount);
	let unconnectedMax = Math.min(unconnectedSlots, unconnectedCount);

	const usedSlots = connectedMax + unconnectedMax;
	const surplusSlots = totalSlots - usedSlots;

	if (surplusSlots > 0) {
		// Give surplus to the section that can take more
		const connectedRemaining = connectedCount - connectedMax;
		const unconnectedRemaining = unconnectedCount - unconnectedMax;

		if (connectedRemaining > 0 && unconnectedRemaining > 0) {
			// Both can take more: favour connected (2:1)
			const toConnected = Math.min(
				surplusSlots,
				connectedRemaining,
				Math.ceil((surplusSlots * ratioConnected) / ratioTotal),
			);
			const toUnconnected = Math.min(surplusSlots - toConnected, unconnectedRemaining);
			connectedMax += toConnected;
			unconnectedMax += toUnconnected;
		} else if (connectedRemaining > 0) {
			connectedMax += Math.min(surplusSlots, connectedRemaining);
		} else if (unconnectedRemaining > 0) {
			unconnectedMax += Math.min(surplusSlots, unconnectedRemaining);
		}
	}

	return { connectedMax, unconnectedMax };
};

export type BuildSingleSelectVisibleAndHiddenParams = {
	visibleAppsFull: FilterOption[];
	connectedApps: FilterOption[];
	unconnectedApps: FilterOption[];
	connectedMax: number;
	unconnectedMax: number;
	hiddenAppsOther: FilterOption[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	sidebarStoredState?: { filters?: Record<string, { hidden?: boolean }> };
	selectedProduct?: string;
	primaryProduct?: string;
};

/**
 * Builds visible and hidden app lists for single-select variant. Takes non-nested unconnected
 * first (nested appear in parent accordions); refills from hidden unconnected when needed to
 * reach unconnectedMax. Keeps nested unconnected in visible for parent accordion population.
 *
 * Respects user visibility decisions: filters out apps that the user has explicitly hidden
 * (via sidebarStoredState) so they don't get pulled back into the visible list by the refill
 * mechanism. This ensures hidden unconnected apps stay hidden and appear in the "Show More" popup.
 *
 * When a product is selected via query params, it is always included in the visible apps,
 * even if it would normally be hidden by viewport constraints.
 */
export const buildSingleSelectVisibleAndHidden = ({
	visibleAppsFull,
	connectedApps,
	unconnectedApps,
	connectedMax,
	unconnectedMax,
	hiddenAppsOther,
	thirdPartyConfigs,
	sidebarStoredState,
	selectedProduct,
	primaryProduct,
}: BuildSingleSelectVisibleAndHiddenParams): {
	visibleApps: FilterOption[];
	hiddenApps: FilterOption[];
} => {
	const nestedProducts = getNestedProducts();

	// Check if an app is explicitly hidden by the user (not just in hiddenAppsOther due to overflow)
	const isUserHidden = (app: FilterOption) =>
		app.value && sidebarStoredState?.filters?.[app.value]?.hidden === true;

	// Pool of unconnected apps we can "refill" from: overflow from main list +
	// unconnected apps that were originally hidden (e.g. Figma, Salesforce)
	// Filter out user-hidden apps from the pool so they don't get pulled back into visible
	const { unconnectedHiddenApps: unconnectedFromHidden } = categorizeAppsByAuthStatus({
		visibleApps: [],
		thirdPartyConfigs,
		hiddenApps: hiddenAppsOther,
	});
	const unconnectedPool = [
		...unconnectedApps.slice(unconnectedMax),
		...unconnectedFromHidden,
	].filter((app) => !isUserHidden(app));

	const isNested = (app: FilterOption) =>
		app.value
			? nestedProducts.has(app.value as ProductKeys) && !NOUN_KEYS_SET.has(app.value)
			: false;

	// First pass: take non-nested unconnected apps (nested ones appear inside parent accordions)
	// Skip apps that are user-hidden
	const unconnectedVisible: FilterOption[] = [];
	for (const app of unconnectedApps) {
		if (unconnectedVisible.length >= unconnectedMax) break;
		if (isNested(app) || isUserHidden(app)) continue;
		unconnectedVisible.push(app);
	}

	// Second pass: refill from pool if we have room (e.g. when many unconnected are nested)
	const promoted = new Set<FilterOption>();
	for (const app of unconnectedPool) {
		if (unconnectedVisible.length >= unconnectedMax) break;
		if (isNested(app) || promoted.has(app)) continue;
		unconnectedVisible.push(app);
		promoted.add(app);
	}

	// Track promoted keys so we don't list them in hiddenApps
	const promotedKeys = new Set(Array.from(promoted).map((a) => a.compositeKey ?? a.value));

	// Keep nested products only within the slice limits to respect viewport constraints
	// When nested products are outside the slice, accordions should collapse (but remain visible)
	// Nouns are excluded: they are standalone inline options, not accordion children
	const unconnectedNestedInSlice = unconnectedApps
		.slice(0, unconnectedMax)
		.filter((app) => app.value && isNested(app) && !isUserHidden(app));

	const connectedNestedInSlice = connectedApps
		.slice(0, connectedMax)
		.filter((app) => app.value && isNested(app) && !isUserHidden(app));

	// Assemble visible: All filter, connected slice, unconnected slice, nested connected, nested unconnected
	// Filter out user-hidden apps from connected apps
	const allOption = visibleAppsFull.find((app) => app.value === undefined);

	// Ensure the selected product is always included in visible apps, even if it would be cut off by viewport limits.
	// Also handles nouns: a noun's value (e.g. 'goal') won't match selectedProduct ('atlas'),
	// so we also check app.isSelected to catch selected nouns that are outside the viewport slice.
	const connectedSlice = connectedApps.slice(0, connectedMax);
	const selectedConnectedApp = selectedProduct
		? connectedApps.find(
				(app) => (app.value === selectedProduct || app.isSelected) && !connectedSlice.includes(app),
			)
		: undefined;
	const selectedUnconnectedApp =
		selectedProduct && !selectedConnectedApp
			? unconnectedApps.find(
					(app) =>
						(app.value === selectedProduct || app.isSelected) && !unconnectedVisible.includes(app),
				)
			: undefined;

	// primaryProduct should always be visible so its accordion shows on initial load.
	// When primaryProduct === selectedProduct, selectedProduct logic already handles it.
	// primaryProduct is always a 1p connected app, never in unconnected
	const primaryProductConnectedApp =
		primaryProduct && primaryProduct !== selectedProduct
			? connectedApps.find(
					(app) =>
						app.value === primaryProduct && !connectedApps.slice(0, connectedMax).includes(app),
				)
			: undefined;

	const visibleApps = [
		...(allOption ? [allOption] : []),
		...connectedApps
			.slice(0, connectedMax)
			.filter(
				(app) =>
					!isUserHidden(app) || app.value === selectedProduct || app.value === primaryProduct,
			),
		...(selectedConnectedApp ? [selectedConnectedApp] : []),
		...(primaryProductConnectedApp ? [primaryProductConnectedApp] : []),
		...unconnectedVisible,
		...(selectedUnconnectedApp ? [selectedUnconnectedApp] : []),
		...connectedNestedInSlice,
		...unconnectedNestedInSlice,
	];

	// Mark parent products with metadata indicating if their nested children are in the visible slice
	// This is used by AccordionFilterItem to determine if accordion should auto-collapse on viewport shrink
	const visibleNestedSet = new Set(
		[
			...connectedNestedInSlice.map((app) => app.value),
			...unconnectedNestedInSlice.map((app) => app.value),
		].filter(Boolean),
	);

	visibleApps.forEach((app) => {
		if (app.value && app.productCategory) {
			const hasVisibleNestedChildren =
				app.productCategory.products?.some((nestedProd: ProductKeys) =>
					visibleNestedSet.has(nestedProd),
				) ?? false;
			app.__hasVisibleChildren = hasVisibleNestedChildren;
		}
	});

	// Hidden = overflow from both sections + originally hidden, minus promoted (already visible) and selected product
	const hiddenAppsRaw = [
		...connectedApps.slice(connectedMax),
		...unconnectedApps.slice(unconnectedMax),
		...hiddenAppsOther,
	];
	const hiddenApps = hiddenAppsRaw.filter((app) => {
		// Exclude promoted apps (already in visible)
		if (promotedKeys.has(app.compositeKey ?? app.value)) {
			return false;
		}
		// Exclude selected product and primary product (always visible)
		if (
			(selectedProduct &&
				(app.value === selectedProduct || app.compositeKey?.startsWith(`${selectedProduct}-`))) ||
			(primaryProduct &&
				(app.value === primaryProduct || app.compositeKey?.startsWith(`${primaryProduct}-`)))
		) {
			return false;
		}
		// Exclude selected nouns (e.g. a noun with isSelected=true that was pulled into visible)
		if (app.isSelected) {
			return false;
		}
		return true;
	});

	return { visibleApps, hiddenApps };
};

export type FilterOption = {
	label: React.ReactNode;
	count?: number;
	value: ProductKeys | undefined;
	isSelected: boolean;
	isPrimaryProduct?: boolean;
	productCategory?: any;
	onSelect: () => void;
	hideFilter: () => void;
	showFilter: () => void;
	resultCountLimit?: number;
	compositeKey?: string;
	nestedApps?: FilterOption[];
	datasourceId?: string;
	section?: Sections;
	countLoading?: boolean;
	timeout?: boolean;
	/**
	 * For single-select variant: indicates if nested children are in the visible slice.
	 * Used to determine if accordion should auto-collapse when viewport shrinks.
	 * Only set by buildSingleSelectVisibleAndHidden for parent products with nested apps.
	 */
	__hasVisibleChildren?: boolean;
};

/**
 * Creates a visible option for a selected datasource when a fanout product is selected
 */
export const createSelectedDatasourceOption = (
	selectedProduct: ProductKeys,
	selectedDatasource: ThirdPartyConfigsWithAPIResponse,
	productFilters: FilterOption[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	onSelectProduct: (
		productKey: ProductKeys | undefined,
		datasource?: ThirdPartyConfigsWithAPIResponse,
	) => void,
): FilterOption | null => {
	const fanoutProduct = productFilters.find(
		(option) =>
			option.value === selectedProduct &&
			option.value &&
			option.value in thirdPartyConfigs &&
			thirdPartyConfigs[option.value as ProductKeys3P]?.fanOutConnectorSources,
	);

	if (!fanoutProduct || !selectedDatasource) {
		return null;
	}

	return {
		...fanoutProduct,
		label:
			selectedDatasource.workspaceName || selectedDatasource.workspaceUrl || fanoutProduct.label,
		compositeKey: `${fanoutProduct.value}-${selectedDatasource.datasourceId}`,
		onSelect: () => onSelectProduct(fanoutProduct.value, selectedDatasource),
		isSelected: true,
		datasourceId: selectedDatasource.datasourceId,
	};
};

/**
 * Whether the Atlassian Web Crawler should be "fanned out" into one option per website
 * datasource. Combines the `webcrawler-fanout-ui-sidebar-enabled` feature gate with the
 * connector's `fanOutConnectorSources` flag — the same gating the right rail applies.
 *
 * Lives here (rather than in a Lite-specific module) so the `fg` call reuses this file's
 * existing feature-flag import instead of introducing a new prohibited barrel import.
 */
export const isWebCrawlerFanoutEnabled = (thirdPartyConfigs: ThirdPartyConfigsBootstrap): boolean =>
	fg('webcrawler-fanout-ui-sidebar-enabled') &&
	Boolean(thirdPartyConfigs[ProductKeysObject.AtlassianWebCrawler]?.fanOutConnectorSources);

/**
 * Checks if an option supports fanout (multiple datasources)
 */
const isFanoutEnabled = (
	option: FilterOption,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): boolean => {
	// Webcrawler fanout is only enabled when the feature flag is on
	if (
		(option.value as string) === ProductKeysObject.AtlassianWebCrawler &&
		!fg('webcrawler-fanout-ui-sidebar-enabled')
	) {
		return false;
	}
	return !!(
		option.value &&
		option.value in thirdPartyConfigs &&
		thirdPartyConfigs[option.value as ProductKeys3P]?.fanOutConnectorSources
	);
};

/**
 * Applies fanout logic to hidden apps for the show more popup
 */
export const applyFanoutToHiddenApps = (
	hiddenApps: FilterOption[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	selectedProduct?: ProductKeys,
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse,
	onSelectProduct?: (
		productKey: ProductKeys | undefined,
		datasource?: ThirdPartyConfigsWithAPIResponse,
	) => void,
): FilterOption[] => {
	// Create a Set for O(1) lookup of the selected datasource
	const selectedDatasourceId = selectedDatasource?.datasourceId;
	const selectedDatasourceSet = selectedDatasourceId ? new Set([selectedDatasourceId]) : new Set();
	return hiddenApps.flatMap((option) => {
		if (isFanoutEnabled(option, thirdPartyConfigs)) {
			const datasources = thirdPartyConfigs[option.value as ProductKeys3P]!.datasources || [];

			const filteredDatasources = [];
			for (let i = 0; i < datasources.length; i++) {
				const datasource = datasources[i];
				if (
					option.value === selectedProduct &&
					selectedDatasourceSet.has(datasource.datasourceId)
				) {
					continue;
				}
				filteredDatasources.push(datasource);
			}

			return filteredDatasources.map((datasource) => {
				const compositeKey = `${option.value}-${datasource.datasourceId}`;
				const isWebCrawler = (option.value as string) === ProductKeysObject.AtlassianWebCrawler;
				const count =
					isWebCrawler || !fg('result-count-for-fanned-out-connectors-enabled')
						? undefined
						: option.count;

				return {
					...option,
					label: datasource.workspaceName || datasource.workspaceUrl || option.label,
					count,
					compositeKey,
					onSelect: onSelectProduct
						? () => onSelectProduct(option.value, datasource)
						: option.onSelect,
					isSelected:
						option.value === selectedProduct && datasource.datasourceId === selectedDatasourceId,
					datasourceId: datasource.datasourceId,
				};
			});
		}
		return [option];
	});
};

/**
 * Creates options for fanout products by expanding them into individual datasource options
 */
export const createFanoutOptions = (
	option: FilterOption,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	selectedProduct?: ProductKeys,
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse,
	onSelectProduct?: (
		productKey: ProductKeys | undefined,
		datasource?: ThirdPartyConfigsWithAPIResponse,
	) => void,
): FilterOption[] => {
	if (
		option.value &&
		option.value in thirdPartyConfigs &&
		thirdPartyConfigs[option.value as ProductKeys3P]?.fanOutConnectorSources
	) {
		const datasources = thirdPartyConfigs[option.value as ProductKeys3P]!.datasources || [];
		const isWebCrawler = (option.value as string) === ProductKeysObject.AtlassianWebCrawler;

		return datasources.map((datasource) => ({
			...option,
			label: datasource.workspaceName || datasource.workspaceUrl || option.label,
			count:
				isWebCrawler || !fg('result-count-for-fanned-out-connectors-enabled')
					? undefined
					: option.count,
			compositeKey: `${option.value}-${datasource.datasourceId}`,
			onSelect: onSelectProduct ? () => onSelectProduct(option.value, datasource) : option.onSelect,
			isSelected:
				option.value === selectedProduct &&
				datasource.datasourceId === selectedDatasource?.datasourceId,
			datasourceId: datasource.datasourceId,
			productCategory: undefined,
		}));
	}

	return [option];
};

/**
 * Categorizes apps by authentication status
 */

type CategorizedApps = {
	productStatuses: Map<ProductKeys, boolean>;
	connectedApps: FilterOption[];
	unconnectedApps: FilterOption[];
	connectedHiddenApps: FilterOption[];
	unconnectedHiddenApps: FilterOption[];
};

export const categorizeAppsByAuthStatus = ({
	visibleApps,
	thirdPartyConfigs,
	hiddenApps,
}: {
	visibleApps: FilterOption[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	hiddenApps?: FilterOption[];
}): CategorizedApps => {
	// First, set product statuses for all apps (visible + hidden)
	const productStatuses = new Map<ProductKeys, boolean>();
	const allApps = [...visibleApps, ...(hiddenApps || [])];

	allApps.forEach((app) => {
		if (!app || app.value === undefined) {
			return;
		}
		// Derive "connected" via isProductConnected (datasource-level), the same
		// derivation used by the Lite app filter bar and the `viewed searchPageFilters`
		// analytics, so connected/unconnected sectioning is consistent across modes.
		const connectStatus = isProductConnected(app.value, thirdPartyConfigs);
		productStatuses.set(app.value, connectStatus);
	});

	// Then, categorize visible apps based on productStatuses
	const connectedApps: FilterOption[] = [];
	const unconnectedApps: FilterOption[] = [];

	visibleApps.forEach((app) => {
		if (!app || app.value === undefined) {
			return;
		}
		const isConnected = productStatuses.get(app.value);
		if (isConnected) {
			connectedApps.push(app);
		} else {
			unconnectedApps.push(app);
		}
	});

	// Categorize hidden apps for separate "Show More" dropdowns in each section
	// This is needed for the `unified_search_right_rail_enhancements` experiment to show the correct apps in the "Show More" dropdowns
	const connectedHiddenApps: FilterOption[] = [];
	const unconnectedHiddenApps: FilterOption[] = [];

	if (hiddenApps) {
		const nestedProducts = getNestedProducts();
		hiddenApps.forEach((app) => {
			if (!app || app.value === undefined) {
				return;
			}
			// Skip nested products (e.g. Confluence DC, Extranet) — they should only appear
			// inside their parent's accordion, never as standalone items in Show More popups.
			// They remain in hiddenApps so allAppsForNesting can still find them for accordion rendering.
			if (nestedProducts.has(app.value as ProductKeys) && !NOUN_KEYS_SET.has(app.value)) {
				return;
			}
			const isConnected = productStatuses.get(app.value);
			if (isConnected) {
				connectedHiddenApps.push(app);
			} else {
				unconnectedHiddenApps.push(app);
			}
		});
	}

	return {
		productStatuses,
		connectedApps,
		unconnectedApps,
		connectedHiddenApps,
		unconnectedHiddenApps,
	};
};
